import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTranslation } from '@pasteltech/i18n-react'
import {
  useAssetTypeList,
  useFundingList,
  useUnitList,
  useUserList,
} from '@stewards-fas/queries'
import {
  AssetStatus,
  AssetTypeResponsePayload,
  Report,
  ReportFunding,
  UnitResponsePayload,
} from '@stewards-fas/schemas'
import { uniq } from 'lodash'
import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { StewardsHeaderLogoLarge } from '../../icons'
import { ReportGeneratorSchemaModel } from '../../models'
import { compareString } from '../../utilities'
import {
  ReportDateTableCell,
  ReportStringTableCell,
  ReportTableCell,
} from './report-table-cell'
import { ReportTableHeader } from './report-table-header'

interface DisplayProps {
  onDisplay?: () => void
  filterValue?: ReportGeneratorSchemaModel
  filterValueNumbmer: number
  reportList: Report[]
}

const Root = styled(TableContainer)`
  margin-bottom: 30px;
  @media print {
    zoom: 0.666;
    @page {
      size: A4 landscape;
    }
  }
`

const Logo = styled(StewardsHeaderLogoLarge)`
  width: 90px;
  height: 90px;
  filter: blur(0);
`

const ReportContainer = styled(Table)`
  display: flex;
  padding: 0 64px;
  flex-direction: column;
  gap: 20px;
`

const ReportHeading = styled(TableRow)`
  display: flex;
  width: 100%;
`

const ReportTitle = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
`

const ReportTitleContent = styled(Typography)`
  display: flex;
  font-weight: 700;
  font-size: 28px;
`

const ReportContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
  margin: 10px 0;
`

const TableIntro = styled(TableRow)`
  display: flex;
  gap: 10px;
  flex-direction: column;
`

const TableIntroContent = styled(Typography)``

type DataPerUnit = {
  assetType: AssetTypeResponsePayload
  dataPerUnitPerAssetType: Report[]
}
type DataWithUnit = {
  unit: UnitResponsePayload
  dataPerUnit: DataPerUnit[]
}
type DisplayDataType = DataWithUnit[]

export const ReportDisplay = memo(
  ({ filterValue, filterValueNumbmer, reportList }: DisplayProps) => {
    const { t } = useTranslation()
    const { data: unitData } = useUnitList()
    const { data: assetTypeData } = useAssetTypeList()
    const allUnits = unitData?.data
    const allAssetTypes = assetTypeData?.data

    const displayReportList = useMemo(() => {
      return reportList
    }, [reportList])

    const getSum = useCallback(
      (unitId?: string, assetTypeId?: string): string => {
        const filteredUnit = unitId
          ? displayReportList.filter((report) => report.unitId === unitId)
          : displayReportList
        const filteredAssetType = assetTypeId
          ? filteredUnit.filter((report) => report.assetTypeId === assetTypeId)
          : filteredUnit
        const sum = filteredAssetType.reduce(
          (acc, it) => acc + it.valuedAmount,
          0,
        )
        return sum.toFixed(2)
      },
      [displayReportList],
    )

    // get all Units appeared in displayReportList
    const displayUnitList: UnitResponsePayload[] = useMemo(() => {
      const uniqIds = uniq(displayReportList.map((r) => r.unitId))
      const uniqUnits = uniqIds
        .map((id) => allUnits?.find((u) => u?.id === id))
        .filter(Boolean) as UnitResponsePayload[]
      const sorted = uniqUnits.sort((a, b) => compareString(a?.code, b?.code))
      return sorted
    }, [displayReportList, allUnits])

    const displayData: DisplayDataType = useMemo(() => {
      return displayUnitList.map((unit) => {
        const dataAllTypes = displayReportList.filter(
          (asset) => asset.unitId === unit?.id,
        )
        const uniqTypeIds = uniq(dataAllTypes.map((d) => d.assetTypeId))
        const uniqAssetTypes = uniqTypeIds
          .map((id) => allAssetTypes?.find((aType) => aType.id === id))
          .sort((ta, tb) => compareString(ta?.code, tb?.code))
          .filter(Boolean) as AssetTypeResponsePayload[]
        const dataOfUnit: DataWithUnit = {
          unit,
          dataPerUnit: uniqAssetTypes.map((aType) => {
            const data: Report[] = displayReportList
              .filter(
                (asset) =>
                  asset.unitId === unit.id && asset.assetTypeId === aType.id,
              )
              .sort((a, b) => compareString(a.assetNo, b.assetNo))
            return {
              assetType: aType,
              dataPerUnitPerAssetType: data,
            }
          }),
        }
        return dataOfUnit
      })
    }, [allAssetTypes, displayReportList, displayUnitList])

    const { data: unitList } = useUnitList()

    const getUnit = useCallback(
      (unitId: string): string => {
        const unit = unitList?.data?.filter((it) => it.id === unitId)[0]
        return `${unit?.code} ${unit?.nameCN}`
      },
      [unitList],
    )

    const { data: userList } = useUserList()

    const getUser = useCallback(
      (userId: string): string => {
        return (
          userList?.data?.filter((it) => it.id === userId)[0]?.username ?? ''
        )
      },
      [userList],
    )

    const getUnitSupervisor = useCallback(
      (unitId: string): string => {
        return getUser(
          unitList?.data?.find((it) => it.id === unitId)?.supervisorUserId ??
            '',
        )
      },
      [getUser, unitList?.data],
    )

    const { data: fundingList } = useFundingList()

    const getFundings = useCallback(
      (fundings: ReportFunding[]): string[] => {
        const fundingIds = fundings.map((it) => it.fundingId)
        const fundingNames: string[] =
          fundingList?.data?.reduce((names, it) => {
            if (fundingIds.includes(it.id)) {
              names.push(it.name ?? '')
            }
            return names
          }, [] as string[]) ?? []
        return fundingNames
      },
      [fundingList?.data],
    )

    const REIMBURSED_RECORD = 'REIMBURSED_RECORD'
    const TRANSITION = 'TRANSITION'

    return (
      <Root>
        <ReportContainer id="Table2XLSX">
          <ReportHeading>
            <Logo />
            <ReportTitle>
              <ReportTitleContent>
                {t('reportPage.titleLine1')}
              </ReportTitleContent>
              <ReportTitleContent>
                {t('reportPage.titleLine2')}
              </ReportTitleContent>
            </ReportTitle>
          </ReportHeading>
          <TableRow />
          {displayData.map(({ unit, dataPerUnit: data }) => {
            return (
              <div key={'table-' + unit.id}>
                <TableIntro>
                  <TableIntroContent>
                    {t('reportPage.content.unit')} :
                    {`${unit.code} ${unit.nameCN}`}
                  </TableIntroContent>
                </TableIntro>
                {data.map(({ assetType, dataPerUnitPerAssetType }) => {
                  return (
                    <ReportContent key={assetType.id}>
                      <TableIntro>
                        <TableIntroContent>
                          {t('reportPage.content.type')} :{' '}
                          {assetType.typeCN ?? ''}
                        </TableIntroContent>
                      </TableIntro>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <ReportTableHeader filterValue={filterValue} />
                          </TableHead>
                          {dataPerUnitPerAssetType.map((it, i) => {
                            return (
                              <TableBody key={it?.id}>
                                <TableRow>
                                  <ReportTableCell>{i + 1}</ReportTableCell>
                                  <ReportStringTableCell>
                                    {it.assetNo}
                                  </ReportStringTableCell>
                                  {filterValue?.name && (
                                    <ReportStringTableCell>
                                      {it?.name}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.brand && (
                                    <ReportStringTableCell>
                                      {it.brand}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.model && (
                                    <ReportStringTableCell>
                                      {it.model}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.placement && (
                                    <ReportStringTableCell>
                                      {it.atUnitNotes}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.buyDate && (
                                    <ReportDateTableCell
                                      value={it.purchasedDateTime}
                                    />
                                  )}
                                  {filterValue?.quantity && (
                                    <ReportTableCell>
                                      {it.quantity}
                                    </ReportTableCell>
                                  )}

                                  {filterValue?.serialNumber && (
                                    <ReportStringTableCell>
                                      {it.serialNo}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.provider && (
                                    <ReportStringTableCell>
                                      {it.providerName}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.invoiceNo && (
                                    <ReportStringTableCell>
                                      {it.invoiceNo}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.warrantyProvider && (
                                    <ReportStringTableCell>
                                      {it.warrantyProvider}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.warrantyPeriod && (
                                    <ReportStringTableCell>
                                      {it.warrantyPeriod}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.sourceOfFundsContent && (
                                    <ReportStringTableCell>
                                      {getFundings(it.assetFundings).join(', ')}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.remarks && (
                                    <ReportStringTableCell>
                                      {it.remarks}
                                    </ReportStringTableCell>
                                  )}

                                  {filterValue?.writeOffDate && (
                                    <ReportDateTableCell
                                      applicable={
                                        it.latestAssetRequestAsset?.assetRequest
                                          ?.type === REIMBURSED_RECORD &&
                                        it.latestAssetRequestAsset?.assetRequest
                                          ?.reimburseStatus === 'APPROVED'
                                      }
                                      value={
                                        it.latestAssetRequestAsset?.assetRequest
                                          ?.lastModifiedDateTime
                                      }
                                    />
                                  )}
                                  {filterValue?.writeOffReason && (
                                    <ReportStringTableCell>
                                      {it.latestAssetRequestAsset?.assetRequest
                                        ?.type === REIMBURSED_RECORD &&
                                      it.latestAssetRequestAsset?.assetRequest
                                        .reimburseStatus === 'APPROVED' ? (
                                        <div>{it.reason}</div>
                                      ) : (
                                        <div>--</div>
                                      )}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.writeOffCode && (
                                    <ReportStringTableCell>
                                      {it.latestAssetRequestAsset?.assetRequest
                                        ?.type === REIMBURSED_RECORD ? (
                                        <div>
                                          {
                                            it.latestAssetRequestAsset
                                              ?.assetRequest?.reimburseNo
                                          }
                                        </div>
                                      ) : (
                                        <div>--</div>
                                      )}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.transferDate && (
                                    <ReportDateTableCell
                                      applicable={
                                        it.latestAssetRequestAsset?.assetRequest
                                          ?.type === TRANSITION
                                      }
                                      value={
                                        it.latestAssetRequestAsset?.assetRequest
                                          ?.transitDateTime
                                      }
                                    />
                                  )}
                                  {filterValue?.transferToUnit && (
                                    <ReportTableCell>
                                      {it.latestAssetRequestAsset?.assetRequest
                                        ?.type === TRANSITION ? (
                                        <div>
                                          {getUnit(
                                            it.latestAssetRequestAsset
                                              ?.assetRequest?.toUnitId,
                                          )}
                                        </div>
                                      ) : (
                                        <div>--</div>
                                      )}
                                    </ReportTableCell>
                                  )}
                                  {filterValue?.transferFromUnit && (
                                    <ReportStringTableCell>
                                      {it.latestAssetRequestAsset?.assetRequest
                                        ?.type === TRANSITION ? (
                                        <div>
                                          {getUnit(
                                            it.latestAssetRequestAsset
                                              ?.assetRequest?.fromUnitId,
                                          )}
                                        </div>
                                      ) : (
                                        <div>--</div>
                                      )}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.transferReason && (
                                    <ReportStringTableCell>
                                      {it.latestAssetRequestAsset?.assetRequest
                                        ?.type === TRANSITION ? (
                                        <div>
                                          {
                                            it.latestAssetRequestAsset
                                              ?.assetRequest?.reason
                                          }
                                        </div>
                                      ) : (
                                        <div>--</div>
                                      )}
                                    </ReportStringTableCell>
                                  )}

                                  {filterValue?.formFiller && (
                                    <ReportStringTableCell>
                                      {getUser(it.formFillUserId)}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.sectionSuperviser && (
                                    <ReportStringTableCell>
                                      {getUnitSupervisor(it.unitId)}
                                    </ReportStringTableCell>
                                  )}
                                  {filterValue?.fillDate && (
                                    <ReportDateTableCell
                                      value={it.createdDateTime}
                                    />
                                  )}
                                  {filterValue?.approvalDate && (
                                    <ReportDateTableCell
                                      applicable={
                                        it.status === AssetStatus.assetApproved
                                      }
                                      value={it.lastModifiedDateTime}
                                    />
                                  )}

                                  {filterValue?.amount && (
                                    <ReportTableCell>
                                      {it.valuedAmount}
                                    </ReportTableCell>
                                  )}
                                </TableRow>
                              </TableBody>
                            )
                          })}
                          {filterValue?.amount && (
                            <TableRow>
                              {Array(filterValueNumbmer + 1).fill(
                                <ReportTableCell />,
                              )}
                              <ReportStringTableCell>
                                {t('reportPage.content.showTotal', {
                                  total: t('reportPage.content.smallTotal'),
                                  sum: getSum(unit.id, assetType.id),
                                })}
                              </ReportStringTableCell>
                            </TableRow>
                          )}
                          <TableRow />
                        </Table>
                      </TableContainer>
                    </ReportContent>
                  )
                })}
              </div>
            )
          })}
          {filterValue?.amount && (
            <TableBody>
              <TableRow>
                {Array.from(
                  { length: filterValueNumbmer + 1 },
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  (_element, i) => (
                    <ReportTableCell key={i} />
                  ),
                )}
                <ReportStringTableCell align="right" sx={{ width: 1 }}>
                  {t('reportPage.content.showTotal', {
                    total: t('reportPage.content.total'),
                    sum: getSum(),
                  })}
                </ReportStringTableCell>
              </TableRow>
            </TableBody>
          )}
        </ReportContainer>
      </Root>
    )
  },
)

ReportDisplay.displayName = 'ReportDisplay'
